import { useEffect, useState } from 'react';
import * as braze from '@braze/web-sdk';
import type { UserInfo } from '@intigral/domain/lib/core/entities/UserInfo';
import {
    flushBrazeData,
    hasValidUserData,
    initializeBraze,
    isBrazeInitialized,
    logBrazeCustomEvent,
    resetBrazeIdentity,
} from '../services/braze/brazeService';

export interface IBrazeContentCard {
    id: string;
    viewed: boolean;
    title: string;
    imageUrl: string;
    description: string;
    created: string;
    updated: string;
    categories: Array<any>;
    expiresAt: string;
    url: any;
    linkText: string;
    aspectRatio: number;
    extras: {
        id: string;
        type: string;
    };
    pinned: boolean;
    dismissible: boolean;
    clicked: boolean;
    dismissed: boolean;
    isControl: boolean;
    test: boolean;
    _: string;
    S: boolean;
}

/**
 * Initializes Braze once CMS config and authenticated user data are available.
 * Runs during render so the SDK is ready before child effects (e.g. content cards) subscribe.
 * Re-identifies automatically when uid replaces accountId on the user object.
 */
export function useBrazeInit(userInfo: UserInfo | null | undefined, configReady: boolean): void {
    if (configReady && hasValidUserData(userInfo)) {
        initializeBraze(userInfo);
    }
}

export function useBrazeCards() {
    const [cards, setCards] = useState<braze.Card[]>([]);

    useEffect(() => {
        if (!isBrazeInitialized()) {
            return;
        }

        braze.subscribeToContentCardsUpdates((event) => {
            setCards(event.cards.filter((card) => !(card as unknown as IBrazeContentCard).clicked));
        });
        braze.requestContentCardsRefresh();
        braze.automaticallyShowInAppMessages();
        braze.getCachedContentCards();
    }, []);

    return cards as unknown as Partial<IBrazeContentCard>[];
}

const useBraze = () => ({
    logBrazeCustomEvent,
    flushBrazeData: async () => {
        await flushBrazeData();
        resetBrazeIdentity();
    },
});

export default useBraze;
